// @ts-nocheck
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { TrainingCategory } from './useTraining';

export interface TrainingFeedCheckin {
  id: string;
  profile_id: string;
  category: TrainingCategory;
  title: string;
  notes: string | null;
  duration_minutes: number | null;
  media_urls: string[] | null;
  created_at: string;
  type: 'checkin';
  profile: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    username: string | null;
  };
}

export interface TrainingFeedRoutine {
  id: string;
  profile_id: string;
  category: string;
  title: string;
  description: string | null;
  routine_data: any;
  media_urls: string[] | null;
  likes_count: number;
  created_at: string;
  type: 'routine';
  profile: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    username: string | null;
  };
  user_has_liked?: boolean;
}

export type TrainingFeedItem = TrainingFeedCheckin | TrainingFeedRoutine;

export type TrainingFeedFilter = 'all' | 'workout' | 'nutrition' | 'recovery' | 'film_study' | 'mental';
export type TrainingFeedSort = 'newest' | 'most_liked';

const PAGE_SIZE = 20;

export function useTrainingFeed(
  filter: TrainingFeedFilter = 'all',
  sort: TrainingFeedSort = 'newest'
) {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const currentUserId = profile?.id;

  // Fetch combined feed with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['training-feed', filter, sort],
    queryFn: async ({ pageParam = 0 }) => {
      const offset = pageParam * PAGE_SIZE;
      
      // Fetch public check-ins
      let checkinsQuery = supabase
        .from('training_checkins')
        .select(`
          id,
          profile_id,
          category,
          title,
          notes,
          duration_minutes,
          media_urls,
          created_at,
          profile:profiles!training_checkins_profile_id_fkey(id, name, avatar_url, username)
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        checkinsQuery = checkinsQuery.eq('category', filter);
      }

      const { data: checkins, error: checkinsError } = await checkinsQuery
        .range(offset, offset + PAGE_SIZE - 1);

      if (checkinsError) throw checkinsError;

      // Fetch public routines
      let routinesQuery = supabase
        .from('training_routines')
        .select(`
          id,
          profile_id,
          category,
          title,
          description,
          routine_data,
          media_urls,
          likes_count,
          created_at,
          profile:profiles!training_routines_profile_id_fkey(id, name, avatar_url, username)
        `)
        .eq('is_public', true)
        .order(sort === 'most_liked' ? 'likes_count' : 'created_at', { ascending: false });

      if (filter !== 'all') {
        routinesQuery = routinesQuery.eq('category', filter);
      }

      const { data: routines, error: routinesError } = await routinesQuery
        .range(offset, offset + PAGE_SIZE - 1);

      if (routinesError) throw routinesError;

      // Check which routines user has liked
      let userLikes: string[] = [];
      if (currentUserId && routines?.length) {
        const { data: likes } = await supabase
          .from('training_routine_likes')
          .select('routine_id')
          .eq('profile_id', currentUserId)
          .in('routine_id', routines.map(r => r.id));
        
        userLikes = (likes || []).map(l => l.routine_id);
      }

      // Add type markers and merge
      const typedCheckins: TrainingFeedCheckin[] = (checkins || []).map(c => ({
        ...c,
        type: 'checkin' as const,
        profile: Array.isArray(c.profile) ? c.profile[0] : c.profile,
      }));

      const typedRoutines: TrainingFeedRoutine[] = (routines || []).map(r => ({
        ...r,
        type: 'routine' as const,
        profile: Array.isArray(r.profile) ? r.profile[0] : r.profile,
        user_has_liked: userLikes.includes(r.id),
      }));

      // Combine and sort
      const combined: TrainingFeedItem[] = [...typedCheckins, ...typedRoutines];
      
      if (sort === 'newest') {
        combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      } else {
        // For most liked, routines come first sorted by likes, then checkins by date
        combined.sort((a, b) => {
          if (a.type === 'routine' && b.type === 'routine') {
            return (b as TrainingFeedRoutine).likes_count - (a as TrainingFeedRoutine).likes_count;
          }
          if (a.type === 'routine') return -1;
          if (b.type === 'routine') return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      }

      return {
        items: combined.slice(0, PAGE_SIZE),
        nextPage: combined.length >= PAGE_SIZE ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
  });

  // Flatten pages into single array
  const feedItems = data?.pages.flatMap(page => page.items) || [];

  // Fetch top routines for sidebar
  const { data: topRoutines } = useQuery({
    queryKey: ['top-routines-week'],
    queryFn: async () => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('training_routines')
        .select(`
          id,
          title,
          category,
          likes_count,
          profile:profiles!training_routines_profile_id_fkey(id, name, avatar_url)
        `)
        .eq('is_public', true)
        .gte('created_at', weekAgo.toISOString())
        .order('likes_count', { ascending: false })
        .limit(5);

      if (error) throw error;
      return (data || []).map(r => ({
        ...r,
        profile: Array.isArray(r.profile) ? r.profile[0] : r.profile,
      }));
    },
  });

  // Fetch followed athletes
  const { data: followedAthletes } = useQuery({
    queryKey: ['training-following', currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];

      const { data, error } = await supabase
        .from('training_follows')
        .select(`
          followed_id,
          profile:profiles!training_follows_followed_id_fkey(id, name, avatar_url, username)
        `)
        .eq('follower_id', currentUserId)
        .limit(10);

      if (error) throw error;
      return (data || []).map(f => ({
        ...f,
        profile: Array.isArray(f.profile) ? f.profile[0] : f.profile,
      }));
    },
    enabled: !!currentUserId,
  });

  // Like routine mutation
  const likeRoutineMutation = useMutation({
    mutationFn: async ({ routineId, hasLiked }: { routineId: string; hasLiked: boolean }) => {
      if (!currentUserId) throw new Error('Must be logged in');

      if (hasLiked) {
        const { error } = await supabase
          .from('training_routine_likes')
          .delete()
          .eq('routine_id', routineId)
          .eq('profile_id', currentUserId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('training_routine_likes')
          .insert({
            routine_id: routineId,
            profile_id: currentUserId,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-feed'] });
      queryClient.invalidateQueries({ queryKey: ['top-routines-week'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to update like', { description: error.message });
    },
  });

  return {
    feedItems,
    isLoading,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
    refetch,
    topRoutines: topRoutines || [],
    followedAthletes: followedAthletes || [],
    likeRoutine: (routineId: string, hasLiked: boolean) => 
      likeRoutineMutation.mutate({ routineId, hasLiked }),
    isLiking: likeRoutineMutation.isPending,
    currentUserId,
  };
}

// Hook for training follow functionality
export function useTrainingFollow() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const currentUserId = profile?.id;

  // Check if following a specific user
  const checkIsFollowing = async (targetId: string): Promise<boolean> => {
    if (!currentUserId) return false;

    const { data } = await supabase
      .from('training_follows')
      .select('id')
      .eq('follower_id', currentUserId)
      .eq('followed_id', targetId)
      .maybeSingle();

    return !!data;
  };

  const followMutation = useMutation({
    mutationFn: async (targetId: string) => {
      if (!currentUserId) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('training_follows')
        .insert({
          follower_id: currentUserId,
          followed_id: targetId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Now following their training');
      queryClient.invalidateQueries({ queryKey: ['training-following'] });
      queryClient.invalidateQueries({ queryKey: ['training-is-following'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to follow', { description: error.message });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: async (targetId: string) => {
      if (!currentUserId) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('training_follows')
        .delete()
        .eq('follower_id', currentUserId)
        .eq('followed_id', targetId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Unfollowed');
      queryClient.invalidateQueries({ queryKey: ['training-following'] });
      queryClient.invalidateQueries({ queryKey: ['training-is-following'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to unfollow', { description: error.message });
    },
  });

  return {
    follow: (targetId: string) => followMutation.mutate(targetId),
    unfollow: (targetId: string) => unfollowMutation.mutate(targetId),
    checkIsFollowing,
    isFollowing: followMutation.isPending,
    isUnfollowing: unfollowMutation.isPending,
    currentUserId,
  };
}
